<template>
  <v-dialog v-model="dialog" max-width="700" @click:outside="cancel">
    <v-card flat v-if="type && coreCard">
      <v-card-title>
        {{ title }}
        <v-spacer></v-spacer>
        <v-btn icon @click="cancel">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-subtitle v-html="subtitle"> </v-card-subtitle>
      <v-card-text>
        <v-card outlined>
          <v-card-text class="pa-0" v-if="type.id == $enums.coreCardTypes.ENPS">
            <v-row class="subtitle-1">
              <v-col cols="auto" class="text-h1 secondary--text ml-2">
                {{ coreCard.value }}
              </v-col>
              <v-col class="my-auto">
                Employee Net Promoter Score (eNPS*)
                <div v-bind:style="{ color: enpsLabel.color }">
                  {{ enpsLabel.label | capitalize }}
                </div>
              </v-col>
              <v-spacer class="limited-spacer"></v-spacer>
              <v-divider vertical></v-divider>
              <v-col cols="auto" class="text-center mr-2 my-auto">
                <div class="text-h3 secondary--text">
                  {{ _.sumBy(coreCard.eNPSItems, "count") }}
                </div>
                <div>responses collected</div>
              </v-col>
            </v-row>
            <v-divider class="mb-4"></v-divider>
            <v-row
              v-for="(item, i) in coreCard.eNPSItems"
              :key="i"
              class="ml-2 subtitle-1"
            >
              <v-col cols="auto">
                <v-icon
                  :color="$coreScores.getEnpsLabelByColumn(item.type).color"
                >
                  {{ $coreScores.getEnpsLabelByColumn(item.type).icon }}
                </v-icon>
              </v-col>
              <v-col cols="6">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-progress-linear
                      class="rounded-lg"
                      :value="(item.count / enpsTotal) * 100"
                      background-color="background"
                      :color="$coreScores.getEnpsLabelByColumn(item.type).color"
                      height="20"
                      v-bind="attrs"
                      v-on="on"
                    >
                    </v-progress-linear>
                  </template>
                  <span class="pa-2">
                    {{ $coreScores.getEnpsLabelByColumn(item.type).name }}
                  </span>
                </v-tooltip>
              </v-col>
              <v-col cols="1" class="secondary--text font-weight-bold">
                {{ Math.round((item.count / enpsTotal) * 100) }}%
              </v-col>
              <v-col cols="auto">
                {{ $coreScores.getEnpsLabelByColumn(item.type).fullName }}
              </v-col>
            </v-row>
            <v-divider class="mt-4"></v-divider>
            <v-row>
              <v-col cols="12" class="px-6 text-center">
                eNPS is on a scale of -100 to 100. The formula for calculating
                eNPS is (% promoters) - (% detractors)
              </v-col>
            </v-row>
          </v-card-text>
          <template
            v-else-if="type.id == $enums.coreCardTypes.ANONYMOUS_FEEDBACK"
          >
            <loading-spinner v-if="loading"></loading-spinner>
            <template v-else>
              <template v-for="(item, i) in feedback">
                <v-card-text :key="i">
                  <v-row dense>
                    <v-col cols="12" class="subtitle-1">
                      {{ item.question }}
                    </v-col>
                    <v-col cols="auto" class="pr-4">
                      <v-chip dark :color="$coreScores.getColor(item.score)">
                        <template v-if="item.isEndOfSurvey">{{
                          item.score | one_decimal
                        }}</template>
                        <template v-else>{{
                          item.score | no_decimal
                        }}</template>
                      </v-chip>
                    </v-col>
                    <v-col class="blur">
                      This response is hidden and can be revealed by upgrading
                      your account. Click "Upgrade to read" to upgrade your
                      account.
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-divider
                  :key="i + 'divider'"
                  v-if="i < feedback.length - 1"
                ></v-divider>
              </template>
            </template>
          </template>
        </v-card>
      </v-card-text>
      <v-card-actions class="px-6 pt-0">
        <v-btn block color="performance" to="/billing">
          {{ upgradeCta }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";
export default {
  name: "UpgradeCoreCardDialog",
  components: {},
  mixins: [],
  props: { coreCard: { type: Object }, type: { type: Object } },
  data: () => ({
    dialog: false,
    loading: false
  }),
  watch: {},
  computed: {
    title() {
      switch (this.type.id) {
        case this.$enums.coreCardTypes.ENPS:
          return `eNPS`;
        case this.$enums.coreCardTypes.ANONYMOUS_FEEDBACK:
          return `Anonymous Feedback Messages`;
        default:
          return "";
      }
    },
    subtitle() {
      switch (this.type.id) {
        case this.$enums.coreCardTypes.ENPS:
          return `How likely is it you would recommend ${this.profile.companyName} as a place to work?`;
        case this.$enums.coreCardTypes.ANONYMOUS_FEEDBACK:
          return `We've collected <span class='performance--text text-decoration-underline'>${this.coreCard.value} written responses</span> from your employees.`;
        default:
          return "";
      }
    },
    upgradeCta() {
      switch (this.type.id) {
        case this.$enums.coreCardTypes.ENPS:
          return `Upgrade for more reporting`;
        case this.$enums.coreCardTypes.ANONYMOUS_FEEDBACK:
          return `Upgrade to read`;
        default:
          return "";
      }
    },
    enpsTotal() {
      return this._.sumBy(this.coreCard.eNPSItems, "count");
    },
    enpsLabel() {
      return this.$coreScores.getEnpsLabel(this.coreCard.value);
    },
    ...mapGetters({ profile: "profile/profile", feedback: "pulse/feedback" })
  },
  methods: {
    loadData() {
      if (this.type.id != this.$enums.coreCardTypes.ANONYMOUS_FEEDBACK) {
        return;
      }
      this.loading = true;
      Promise.all([
        this.$store.dispatch("pulse/getPulseFeedback", {
          timeframeId: this.$enums.timeframes.LAST_90_DAYS,
          acknowledged: false,
          customStartDate: null,
          customEndDate: null,
          customTimezone: moment.tz.guess(true)
        })
      ])
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occured while loading feedback."
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
    cancel() {
      this.dialog = false;
      this.$emit("cancel");
    },
    showDialog() {
      this.dialog = true;
      this.loadData();
    }
  }
};
</script>

<style lang="scss" scoped>
.blur {
  color: transparent;
  text-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
}
</style>
